import { Navigate, RouteObject } from 'react-router-dom';
import { WorkspaceErrorElement } from '../components/errors/RouteErrorElement';
import { loadRouteChunk } from '../forge/utils/lazyRouteModule';
import { EvalLayout } from './components/layout/EvalLayout';
import { prCompRoutes } from './pr-compliance/routes';

// Page routes load lazily (route.lazy) so each Eval page is its own chunk and
// the main bundle stays free of Eval page code. The router awaits the chunk
// during navigation, so no Suspense fallback is needed. The `/eval` layout
// element stays eager: it is the shell that has to render while a page chunk
// is still in flight.
export const evalRoutes: RouteObject = {
  path: '/eval',
  element: <EvalLayout />,
  children: [
    {
      // Pathless, so it adds no segment: it exists only to own `errorElement`.
      // Sitting *inside* the layout route means a page crash replaces the
      // content area while the Eval sidebar stays mounted and navigable.
      errorElement: <WorkspaceErrorElement homePath="/eval/runs" homeLabel="Back to runs" />,
      children: [
        { index: true, element: <Navigate to="runs" replace /> },
        {
          path: 'runs',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunListPage'))).RunListPage,
          }),
        },
        {
          path: 'runs/new',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunSchedulerPage')))
              .RunSchedulerPage,
          }),
        },
        {
          path: 'runs/compare',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/CompareRunsPage')))
              .CompareRunsPage,
          }),
        },
        {
          path: 'runs/compare/errors',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/CompareRunErrorsPage')))
              .CompareRunErrorsPage,
          }),
        },
        {
          path: 'runs/compare/subtasks',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/CompareRunSubtasksPage')))
              .CompareRunSubtasksPage,
          }),
        },
        {
          path: 'runs/:runId',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunDetailPage')))
              .RunDetailPage,
          }),
        },
        {
          path: 'runs/:runId/errors',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunErrorAnalysisPage')))
              .RunErrorAnalysisPage,
          }),
        },
        {
          path: 'runs/:runId/subtasks',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunSubtaskAnalysisPage')))
              .RunSubtaskAnalysisPage,
          }),
        },
        {
          path: 'runs/:runId/post-processing',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (
              await loadRouteChunk(() => import('./pages/runs/PostProcessingAnalysisPage'))
            ).PostProcessingAnalysisPage,
          }),
        },
        {
          path: 'runs/:runId/inferences/:runInferenceId',
          handle: { pageWidth: 'full' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunInferenceSamplePage')))
              .RunInferenceSamplePage,
          }),
        },
        {
          path: 'tasks/pareto',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/tasks/TaskParetoPage')))
              .TaskParetoPage,
          }),
        },
        {
          path: 'datasets',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/datasets/DatasetListPage')))
              .DatasetListPage,
          }),
        },
        {
          path: 'datasets/new',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/datasets/DatasetCreatePage')))
              .DatasetCreatePage,
          }),
        },
        {
          path: 'datasets/:datasetId',
          handle: { pageWidth: 'full' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/datasets/DatasetDetailPage')))
              .DatasetDetailPage,
          }),
        },
        {
          path: 'gt-samples',
          handle: { pageWidth: 'full' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/gt-samples/GtSamplesListPage')))
              .GtSamplesListPage,
          }),
        },
        {
          path: 'gt-samples/:gtSampleId',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/gt-samples/GtSamplePage')))
              .GtSamplePage,
          }),
        },
        {
          path: 'import/pr-evaluation-samples',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (
              await loadRouteChunk(() => import('./pages/gt-samples/PrEvaluationImportPage'))
            ).PrEvaluationImportPage,
          }),
        },
        {
          path: 'pr-inferences',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (
              await loadRouteChunk(() => import('./pages/gt-inferences/InferencePickerPage'))
            ).InferencePickerPage,
          }),
        },
        { path: 'models', element: <Navigate to="/eval/od-models" replace /> },
        {
          path: 'od-models',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/RayModelListPage')))
              .OdModelListPage,
          }),
        },
        {
          path: 'pr-models',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/RayModelListPage')))
              .PrModelListPage,
          }),
        },
        {
          path: 'oos-models',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/RayModelListPage')))
              .OosModelListPage,
          }),
        },
        {
          path: 'models/:modelId',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/ModelDetailPage')))
              .ModelDetailPage,
          }),
        },
        {
          path: 'pc-configs',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/PcConfigsListPage')))
              .PcConfigsListPage,
          }),
        },
        {
          path: 'error-labels',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/ErrorLabelsPage')))
              .ErrorLabelsPage,
          }),
        },
        {
          path: 'metrics',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/metrics/MetricsPage')))
              .MetricsPage,
          }),
        },
        ...prCompRoutes,
      ],
    },
  ],
};
